import {
    type ReactElement,
    isValidElement,
    type ReactNode,
    Children,
    Fragment
} from "react";
import type {
    CommandVisibleConstantType,
    CommandNormalizedType,
    CommandPageModelType,
    CommandEntryItemType,
    CommandItemModelType,
    ICommandLoadingProps,
    CommandShortcutType,
    CommandCollectType,
    ICommandGroupProps,
    ICommandEmptyProps,
    ICommandInputProps,
    ICommandItemProps,
    ICommandPageProps,
    CommandMatchType,
    CommandModelType,
    CommandBlockType,
    CommandPartType
} from "./type";
import type {
    KbdPlatform
} from "../kbd/type";

export const COMMAND_LOCALE = "tr-TR";

export const COMMAND_ROOT_SCOPE = "root";

export const COMMAND_EMPTY_SCOPE = "empty";

const COMMAND_WORD_SEPARATOR = /[\s·›/\-_.,:;()]/;

const COMMAND_SHORTCUT_APPLE_LABELS: Record<string, string> = {
    shift: "⇧",
    ctrl: "⌃",
    mod: "⌘",
    alt: "⌥"
};

const COMMAND_SHORTCUT_OTHER_LABELS: Record<string, string> = {
    shift: "Shift",
    ctrl: "Ctrl",
    mod: "Ctrl",
    alt: "Alt"
};

const COMMAND_SHORTCUT_ARIA_NAMES: Record<KbdPlatform, Record<string, string>> = {
    apple: {
        ctrl: "Control",
        shift: "Shift",
        mod: "Meta",
        alt: "Alt"
    },
    other: {
        ctrl: "Control",
        mod: "Control",
        shift: "Shift",
        alt: "Alt"
    }
};

const COMMAND_SHORTCUT_ALIASES: Record<string, string> = {
    control: "ctrl",
    option: "alt",
    cmd: "mod"
};

export const getCommandPart = (element: ReactElement): CommandPartType | undefined => {
    const elementType: unknown = element.type;

    if(typeof elementType !== "function" && (typeof elementType !== "object" || elementType === null)) {
        return undefined;
    }

    if(!("commandPart" in elementType)) {
        return undefined;
    }

    const part = elementType.commandPart;

    if(typeof part !== "string") {
        return undefined;
    }

    return part as CommandPartType;
};

export const normalizeCommandText = ({
    text
}: {
    text: string;
}): CommandNormalizedType => {
    const starts: Array<number> = [];

    const ends: Array<number> = [];

    let normalized = "";

    let index = 0;

    for(const char of text) {
        const nextChar = char.toLocaleLowerCase(COMMAND_LOCALE).normalize("NFD").replace(/\p{M}/gu, "").replace(/ı/g, "i");

        for(let unit = 0; unit < nextChar.length; unit++) {
            starts.push(index);

            ends.push(index + char.length);
        }

        normalized = `${normalized}${nextChar}`;

        index += char.length;
    }

    return {
        text: normalized,
        starts,
        ends
    };
};

export const getCommandMatch = ({
    parentTitle,
    keywords,
    query,
    title
}: {
    keywords?: Array<string>;
    parentTitle?: string;
    query: string;
    title: string;
}): CommandMatchType | undefined => {
    const normalizedQuery = normalizeCommandText({
        text: query.trim()
    }).text;

    if(!normalizedQuery) {
        return {
            start: 0,
            rank: 0,
            end: 0
        };
    }

    const normalizedTitle = normalizeCommandText({
        text: title
    });

    let wordStart = -1;

    let searchFrom = 0;

    while(searchFrom <= normalizedTitle.text.length) {
        const index = normalizedTitle.text.indexOf(normalizedQuery, searchFrom);

        if(index === -1) {
            break;
        }

        const previousChar = normalizedTitle.text.charAt(index - 1);

        if(index === 0 || COMMAND_WORD_SEPARATOR.test(previousChar)) {
            wordStart = index;

            break;
        }

        searchFrom = index + 1;
    }

    const substringIndex = wordStart === -1 ? normalizedTitle.text.indexOf(normalizedQuery) : wordStart;

    if(substringIndex !== -1) {
        return {
            end: normalizedTitle.ends[substringIndex + normalizedQuery.length - 1] ?? title.length,
            start: normalizedTitle.starts[substringIndex] ?? 0,
            rank: wordStart === -1 ? 1 : 0
        };
    }

    const hiddenTerms = [
        ...(keywords ?? []),
        ...(parentTitle ? [
            parentTitle
        ] : [])
    ];

    const isHiddenMatch = hiddenTerms.some((term) => normalizeCommandText({
        text: term
    }).text.includes(normalizedQuery));

    if(!isHiddenMatch) {
        return undefined;
    }

    return {
        start: 0,
        rank: 2,
        end: 0
    };
};

export const parseCommandShortcut = ({
    shortcut
}: {
    shortcut: string;
}): CommandShortcutType => {
    const isEndingWithPlus = shortcut.trim() === "+" || shortcut.trimEnd().endsWith("++");

    const parts = (isEndingWithPlus ? shortcut.trimEnd().slice(0, -1) : shortcut).split("+").map((part) => part.trim().toLowerCase()).filter((part) => part);

    const modifiers = parts.map((part) => COMMAND_SHORTCUT_ALIASES[part] ?? part);

    const key = isEndingWithPlus ? "+" : modifiers.filter((part) => !(part in COMMAND_SHORTCUT_OTHER_LABELS)).pop() ?? "";

    return {
        isShift: modifiers.includes("shift"),
        isCtrl: modifiers.includes("ctrl"),
        isAlt: modifiers.includes("alt"),
        isMod: modifiers.includes("mod"),
        key
    };
};

export const getCommandShortcutLabel = ({
    platform,
    shortcut
}: {
    platform: KbdPlatform;
    shortcut: string;
}): string => {
    const parsed = parseCommandShortcut({
        shortcut
    });

    const labels = platform === "apple" ? COMMAND_SHORTCUT_APPLE_LABELS : COMMAND_SHORTCUT_OTHER_LABELS;

    const modifiers = [
        parsed.isCtrl ? labels.ctrl : "",
        parsed.isAlt ? labels.alt : "",
        parsed.isShift ? labels.shift : "",
        parsed.isMod ? labels.mod : ""
    ].filter((label) => label);

    const keyLabel = parsed.key.length === 1 ? parsed.key.toUpperCase() : parsed.key;

    if(platform === "apple") {
        return `${modifiers.join("")}${keyLabel}`;
    }

    return [
        ...modifiers,
        keyLabel
    ].join("+");
};

export const getCommandShortcutAria = ({
    platform,
    shortcut
}: {
    platform: KbdPlatform;
    shortcut: string;
}): string => {
    const parsed = parseCommandShortcut({
        shortcut
    });

    const names = COMMAND_SHORTCUT_ARIA_NAMES[platform];

    const keyName = parsed.key.length === 1 ? parsed.key.toUpperCase() : parsed.key;

    return [
        parsed.isCtrl ? names.ctrl : "",
        parsed.isAlt ? names.alt : "",
        parsed.isShift ? names.shift : "",
        parsed.isMod ? names.mod : "",
        keyName
    ].filter((name) => name).join("+");
};

export const getIsCommandShortcutPressed = ({
    platform,
    shortcut,
    event
}: {
    platform: KbdPlatform;
    event: KeyboardEvent;
    shortcut: string;
}): boolean => {
    if(!event.key) {
        return false;
    }

    const parsed = parseCommandShortcut({
        shortcut
    });

    const isApple = platform === "apple";

    const isModPressed = isApple ? event.metaKey : event.ctrlKey;

    const isCtrlPressed = parsed.isMod && !isApple ? isModPressed : event.ctrlKey;

    const expectedCtrl = parsed.isCtrl || (parsed.isMod && !isApple);

    const expectedMeta = parsed.isMod && isApple;

    return event.key.toLowerCase() === parsed.key
        && isCtrlPressed === expectedCtrl
        && event.metaKey === expectedMeta
        && event.shiftKey === parsed.isShift
        && event.altKey === parsed.isAlt;
};

export const getIsCommandShortcutClaimed = ({
    shortcut,
    target
}: {
    target: EventTarget | null;
    shortcut: string;
}): boolean => {
    if(typeof Element === "undefined" || !(target instanceof Element)) {
        return false;
    }

    const owner = target.closest("[data-ncore-shortcuts]");

    if(!owner) {
        return false;
    }

    const claimedShortcuts = (owner.getAttribute("data-ncore-shortcuts") ?? "").toLowerCase().split(" ");

    return claimedShortcuts.includes(shortcut.toLowerCase());
};

const getCommandChildElements = (node: ReactNode): Array<ReactElement> => {
    const elements: Array<ReactElement> = [];

    Children.forEach(node, (child) => {
        if(isValidElement(child)) {
            elements.push(child);
        }
    });

    return elements;
};

const getCommandItemKey = ({
    counter,
    element,
    scope
}: {
    counter: CommandCollectType["counter"];
    element: ReactElement;
    scope: string;
}): string => {
    counter.value += 1;

    return element.key === null ? `${scope}-${counter.value}` : `${scope}-${element.key}`;
};

const collectCommandChildren = ({
    children,
    state
}: {
    state: CommandCollectType;
    children: ReactNode;
}) => {
    getCommandChildElements(children).forEach((element) => {
        if(element.type === Fragment) {
            collectCommandChildren({
                children: (element.props as {
                    children?: ReactNode;
                }).children,
                state
            });

            return;
        }

        const part = getCommandPart(element);

        const entries = state.model.scopes[state.scope] ?? [];

        state.model.scopes[state.scope] = entries;

        const key = getCommandItemKey({
            counter: state.counter,
            scope: state.scope,
            element
        });

        switch(part) {
            case "list":
                collectCommandChildren({
                    children: (element.props as {
                        children?: ReactNode;
                    }).children,
                    state
                });

                break;
            case "input":
                if(!state.model.inputProps) {
                    state.model.inputProps = element.props as ICommandInputProps;
                }

                break;
            case "item":
                entries.push({
                    item: {
                        ...(element.props as ICommandItemProps),
                        itemKey: key
                    },
                    key
                });

                break;
            case "group": {
                const groupProps = element.props as ICommandGroupProps;

                entries.push({
                    group: {
                        items: getCommandChildElements(groupProps.children).filter((child) => getCommandPart(child) === "item").map((child, index) => ({
                            ...(child.props as ICommandItemProps),
                            itemKey: child.key === null ? `${key}-${index}` : `${key}-${child.key}`
                        })),
                        displayBehaviour: groupProps.displayBehaviour ?? "always",
                        title: groupProps.title,
                        key
                    },
                    key
                });

                break;
            }
            case "separator":
                entries.push({
                    isSeparator: true,
                    key
                });

                break;
            case "page": {
                const pageProps = element.props as ICommandPageProps;

                const page: CommandPageModelType = {
                    placeholder: pageProps.placeholder,
                    key: pageProps.pageKey,
                    title: pageProps.title,
                    entries: []
                };

                state.model.pages[pageProps.pageKey] = page;

                collectCommandChildren({
                    children: pageProps.children,
                    state: {
                        scope: pageProps.pageKey,
                        counter: state.counter,
                        model: state.model
                    }
                });

                page.entries = state.model.scopes[pageProps.pageKey] ?? [];

                break;
            }
            case "empty": {
                const emptyProps = element.props as ICommandEmptyProps;

                state.model.emptyProps = emptyProps;

                collectCommandChildren({
                    state: {
                        scope: COMMAND_EMPTY_SCOPE,
                        counter: state.counter,
                        model: state.model
                    },
                    children: emptyProps.children
                });

                break;
            }
            case "loading":
                state.model.loadingProps = element.props as ICommandLoadingProps;

                break;
            default:
                break;
        }
    });
};

export const buildCommandModel = ({
    children
}: {
    children: ReactNode;
}): CommandModelType => {
    const model: CommandModelType = {
        scopes: {
        },
        pages: {
        }
    };

    collectCommandChildren({
        state: {
            scope: COMMAND_ROOT_SCOPE,
            counter: {
                value: 0
            },
            model
        },
        children
    });

    return model;
};

const getCommandItemModel = ({
    filterBehaviour,
    idPrefix,
    item,
    query
}: {
    filterBehaviour: CommandVisibleConstantType["filterBehaviour"];
    item: CommandEntryItemType;
    idPrefix: string;
    query: string;
}): CommandItemModelType | undefined => {
    const match = filterBehaviour === "manual" ? {
        start: 0,
        rank: 0,
        end: 0
    } : getCommandMatch({
        parentTitle: item.parentTitle,
        keywords: item.keywords,
        title: item.title,
        query
    });

    if(!match) {
        return undefined;
    }

    return {
        isDisabled: item.isDisabled === true,
        id: `${idPrefix}-${item.itemKey}`,
        key: item.itemKey,
        props: item,
        match
    };
};

export const getCommandVisibleBlocks = ({
    filterBehaviour,
    idPrefix,
    entries,
    query
}: CommandVisibleConstantType): Array<CommandBlockType> => {
    const blocks: Array<CommandBlockType> = [];

    const isSearching = query.length > 0;

    let isPendingSeparator = false;

    const pushBlock = (block: CommandBlockType) => {
        if(isPendingSeparator && blocks.length) {
            blocks.push({
                key: `${block.key}-separator`,
                isSeparator: true,
                items: [],
                id: ""
            });
        }

        isPendingSeparator = false;

        blocks.push(block);
    };

    entries.forEach((entry) => {
        if(entry.isSeparator) {
            isPendingSeparator = true;

            return;
        }

        if(entry.item) {
            const item = getCommandItemModel({
                item: entry.item,
                filterBehaviour,
                idPrefix,
                query
            });

            if(item) {
                pushBlock({
                    id: `${idPrefix}-${entry.key}`,
                    isSeparator: false,
                    key: entry.key,
                    items: [
                        item
                    ]
                });
            }

            return;
        }

        const group = entry.group;

        if(!group || (group.displayBehaviour === "idle" && isSearching)) {
            return;
        }

        const items = group.items.map((item) => getCommandItemModel({
            filterBehaviour,
            idPrefix,
            query,
            item
        })).filter((item): item is CommandItemModelType => item !== undefined);

        if(filterBehaviour === "auto" && isSearching) {
            items.sort((first, second) => (first.match?.rank ?? 0) - (second.match?.rank ?? 0));
        }

        if(!items.length) {
            return;
        }

        pushBlock({
            id: `${idPrefix}-${entry.key}`,
            isSeparator: false,
            title: group.title,
            key: entry.key,
            items
        });
    });

    return blocks;
};

export const getCommandNavigableItems = ({
    blocks
}: {
    blocks: Array<CommandBlockType>;
}): Array<CommandItemModelType> => {
    return blocks.flatMap((block) => block.items).filter((item) => !item.isDisabled);
};
